/**
 * Alshallal Pastry Company HR System - Module Policy Context
 * Phase 18.10: Module Visibility Controls
 * 
 * Provides module visibility state across the application.
 * Used by sidebar and route guards to show/hide modules.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ModulePolicyContext = createContext(null);

export const useModulePolicy = () => {
  const context = useContext(ModulePolicyContext);
  if (!context) {
    throw new Error('useModulePolicy must be used within a ModulePolicyProvider');
  }
  return context;
};

export const ModulePolicyProvider = ({ children }) => {
  const { token, user, isAuthenticated } = useAuth();
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch module policy for current user's role
  const fetchMyModules = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setModules({});
      setLoading(false);
      return;
    }

    // The login mode is still unanswered, so every route including this one is
    // refused. Asking would only log a 403 and then fall through to "all
    // enabled", which is the wrong default to sit behind the chooser.
    if (user?.login_mode_required) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/module-policy/my-modules`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setModules(data.modules || {});
      } else {
        // If policy endpoint fails, default to all enabled
        // This ensures backward compatibility
        console.warn('Module policy endpoint failed, defaulting to enabled');
        setModules({});
      }
    } catch (err) {
      console.error('Error fetching module policy:', err);
      setError(err.message);
      // Default to empty (no restrictions) on error
      setModules({});
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, user?.login_mode_required]);
  
  useEffect(() => {
    fetchMyModules();
    // Switching mode changes the effective role, so the menu has to be re-read.
  }, [fetchMyModules, user?.role, user?.login_mode]);
  
  /**
   * Check if a module is enabled for the current user.
   * Returns true if:
   * - Policy not loaded yet (default to enabled)
   * - Module is explicitly enabled
   * - Module key not in policy (default to enabled for backward compatibility)
   */
  const isModuleEnabled = useCallback((moduleKey) => {
    // If still loading or error, default to enabled
    if (loading || error) return true;
    
    // If no policy set, default to enabled
    if (Object.keys(modules).length === 0) return true;
    
    // Check policy
    const enabled = modules[moduleKey];
    return enabled !== false; // Default to true if not explicitly false
  }, [modules, loading, error]);
  
  /**
   * Check if multiple modules are enabled (any of them)
   */
  const isAnyModuleEnabled = useCallback((moduleKeys) => {
    return moduleKeys.some(key => isModuleEnabled(key));
  }, [isModuleEnabled]);
  
  /**
   * Refresh module policy (call after policy changes)
   */
  const refresh = useCallback(() => {
    fetchMyModules();
  }, [fetchMyModules]);
  
  const value = {
    modules,
    loading,
    error,
    isModuleEnabled,
    isAnyModuleEnabled,
    refresh,
  };
  
  return (
    <ModulePolicyContext.Provider value={value}>
      {children}
    </ModulePolicyContext.Provider>
  );
};

export default ModulePolicyContext;
